import { Dispatch, SetStateAction } from "react";

import { useGalleryImage2 } from "../../../../api/api-hooks/images/useGalleryImage";
import useRecord from "../../../../api/api-hooks/records/useRecord";
import { HandleTranscribeProps } from "../../GrowthDriveInterfaces";
import { FileInterface } from "../../../../types/RecordInterfaces";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import ImageCardListUploading from "./ImageCardListUploading";
import ImageCardUploading from "./ImageCardUploading";
import ImageCardList from "./ImageCardList";
import ImageCardGrid from "./ImageCardGrid";
import { FileRecord } from "../../Library";

export interface ImageCardProps {
  fileID: string;
  file: FileInterface;
  setPreview: Dispatch<SetStateAction<number | null>>;
  setOpenInfo: Dispatch<SetStateAction<FileRecord | null>>;
  deleteClick: ({ recordID }: { recordID: string }) => void;
  setFileToTranslate:
    | Dispatch<SetStateAction<FileInterface | null>>
    | undefined;
  selectedItems: FileRecord[];
  onSelect: Dispatch<SetStateAction<FileRecord[]>>;
  viewType: "List" | "Grid";
  index: number;
  multiFiles?: boolean;
  handleTranscribe: (props: HandleTranscribeProps) => void;
}

const ImageCard = ({
  setFileToTranslate,
  setPreview,
  setOpenInfo,
  deleteClick,
  handleTranscribe,
  onSelect,
  file,
  selectedItems,
  viewType,
  fileID,
  index,
  multiFiles,
}: ImageCardProps) => {
  const { record: contact } = useRecord({
    moduleAPIName: "Contacts",
    recordID:
      typeof file?.UploadedBy === "string"
        ? file?.UploadedBy
        : file?.UploadedBy?.id,
    enabled: typeof file?.UploadedBy === "string",
  });
  const { fetchedFile, loading } = useGalleryImage2(file);

  if (file?.Uploading) {
    return viewType === "List" ? (
      <ImageCardListUploading file={file} />
    ) : (
      <ImageCardUploading file={file} />
    );
  }

  if (loading) {
    if (viewType === "Grid") {
      return <SkeletonLoader css="tw:w-52.5 tw:h-45" />;
    } else {
      return (
        <SkeletonLoader css="tw:w-full tw:md:w-70 tw:lg:w-76 tw:xl:w-88 tw:h-[100px]" />
      );
    }
  }

  const handleSelect = () => {
    onSelect((prev) => {
      if (prev.find((s) => s.recordID === fileID)) {
        return prev.filter((s) => s.recordID !== fileID);
      }

      if (multiFiles === false) {
        return [{ recordID: fileID, file: fetchedFile }];
      }

      return [...prev, { recordID: fileID, file: fetchedFile }];
    });
  };

  function handleOpenPreview() {
    setPreview(index);
  }

  function handleOpenInfo() {
    setOpenInfo({ file: fetchedFile, recordID: fileID });
  }

  function handleDelete() {
    deleteClick({
      recordID: fileID,
    });
  }

  function onTranscribe(setTranscribing: Dispatch<SetStateAction<boolean>>) {
    handleTranscribe({
      fileID: fileID,
      file: fetchedFile,
      setTranscribing,
    });
  }

  const isSelected = selectedItems.some((s) => s.recordID === fileID);

  if (viewType === "Grid") {
    return (
      <ImageCardGrid
        onClick={handleOpenPreview}
        infoClick={handleOpenInfo}
        deleteClick={handleDelete}
        fetchedFile={fetchedFile}
        setFileToTranslate={setFileToTranslate}
        contact={contact || file.UploadedBy}
        isSelected={isSelected}
        handleSelect={handleSelect}
        onTranscribe={onTranscribe}
      />
    );
  }

  return (
    <ImageCardList
      onClick={handleOpenPreview}
      infoClick={handleOpenInfo}
      deleteClick={handleDelete}
      fetchedFile={fetchedFile}
      setFileToTranslate={setFileToTranslate}
      contact={contact || file.UploadedBy}
      isSelected={isSelected}
      handleSelect={handleSelect}
      onTranscribe={onTranscribe}
    />
  );
};

export default ImageCard;
